import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import Index from "./pages/Index";
import AboutPage from "./pages/AboutPage";
import TreatmentsPage from "./pages/TreatmentsPage";
import ContactPage from "./pages/ContactPage";
import NotFound from "./pages/NotFound";
import AsthmaPage from "./pages/AsthmaPage";
import BackPainPage from "./pages/BackPainPage";
import BronchitisPage from "./pages/BronchitisPage";
import CervicalSpondylosisPage from "./pages/CervicalSpondylosisPage";
import DiabetesPage from "./pages/DiabetesPage";
import DiscProblemsPage from "./pages/DiscProblemsPage";
import EczemaPage from "./pages/EczemaPage";
import EpilepsyPage from "./pages/EpilepsyPage";
import FattyLiverPage from "./pages/FattyLiverPage";
import FistulaPage from "./pages/FistulaPage";
import FrozenShoulderPage from "./pages/FrozenShoulderPage";
import KneeJointPainPage from "./pages/KneeJointPainPage";
import MigrainePage from "./pages/MigrainePage";
import ObesityPage from "./pages/ObesityPage";
import OtherSkinDiseasesPage from "./pages/OtherSkinDiseasesPage";
import PeripheralNeuritisPage from "./pages/PeripheralNeuritisPage";
import PimplesPage from "./pages/PimplesPage";
import ParkinsonismPage from "./pages/ParkinsonismPage";
import TonsillitisPage from "./pages/TonsillitisPage";
import UlcerPage from "./pages/UlcerPage";
import VaricoseVeinsPage from "./pages/VaricoseVeinsPage";
import PCODPage from "./pages/PCODPage";
import AcidRefluxPage from "./pages/AcidRefluxPage";
import GastritisPage from "./pages/GastritisPage";
import HeartBurnPage from "./pages/HeartBurnPage";
import HormonalImbalancePage from "./pages/HormonalImbalancePage";
import UrticariaPage from "./pages/UrticariaPage";
import WormInfestationPage from "./pages/WormInfestationPage";
import CellulitisPage from "./pages/CellulitisPage";
import ConstipationPage from "./pages/ConstipationPage";
import FibroidsPage from "./pages/FibroidsPage";
import WhiteDischargePage from "./pages/WhiteDischargePage";
import ParalysisPage from "./pages/ParalysisPage";
import PsoriasisPage from "./pages/PsoriasisPage";
import SpondylosisPage from "./pages/SpondylosisPage";
import ArthritisPage from "./pages/ArthritisPage";
import AnalFissurePage from "./pages/AnalFissurePage";
import CalcanealSpurPage from "./pages/CalcanealSpurPage";
import DiabeticWoundPage from "./pages/DiabeticWoundPage";
import DandruffPage from "./pages/DandruffPage";
import DigestiveDisordersPage from "./pages/DigestiveDisordersPage";
import DyslipidemiaPage from "./pages/DyslipidemiaPage";
import FemaleDisordersPage from "./pages/FemaleDisordersPage";
import FungalInfectionPage from "./pages/FungalInfectionPage";
import GallBladderStonesPage from "./pages/GallBladderStonesPage";
import HairFallPage from "./pages/HairFallPage";
import HypertensionPage from "./pages/HypertensionPage";
import HydrocelePage from "./pages/HydrocelePage";
import InfertilityPage from "./pages/InfertilityPage";
import KidneyStonesPage from "./pages/KidneyStonesPage";
import LeucodermaPage from "./pages/LeucodermaPage";
import LipomaPage from "./pages/LipomaPage";
import PilesPage from "./pages/PilesPage";
import SinusitisPage from "./pages/SinusitisPage";
import ThyroidPage from "./pages/ThyroidPage";
import TrigeminalNeuralgiaPage from "./pages/TrigeminalNeuralgiaPage";
import VaricoseUlcerPage from "./pages/VaricoseUlcerPage";
import GalleryPage from '@/pages/GalleryPage';
import ShopPage from '@/pages/ShopPage';

const queryClient = new QueryClient();

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/treatments" element={<TreatmentsPage />} />
              <Route path="/treatments/asthma" element={<AsthmaPage />} />
              <Route path="/treatments/back-pain" element={<BackPainPage />} />
              <Route path="/treatments/bronchitis" element={<BronchitisPage />} />
              <Route path="/treatments/cervical-spondylosis" element={<CervicalSpondylosisPage />} />
              <Route path="/treatments/diabetes" element={<DiabetesPage />} />
              <Route path="/treatments/disc-problems" element={<DiscProblemsPage />} />
              <Route path="/treatments/eczema" element={<EczemaPage />} />
              <Route path="/treatments/epilepsy" element={<EpilepsyPage />} />
              <Route path="/treatments/fatty-liver" element={<FattyLiverPage />} />
              <Route path="/treatments/fistula" element={<FistulaPage />} />
              <Route path="/treatments/frozen-shoulder" element={<FrozenShoulderPage />} />
              <Route path="/treatments/knee-joint-pain" element={<KneeJointPainPage />} />
              <Route path="/treatments/migraine" element={<MigrainePage />} />
              <Route path="/treatments/obesity" element={<ObesityPage />} />
              <Route path="/treatments/other-skin-diseases" element={<OtherSkinDiseasesPage />} />
              <Route path="/treatments/peripheral-neuritis" element={<PeripheralNeuritisPage />} />
              <Route path="/treatments/pimples" element={<PimplesPage />} />
              <Route path="/treatments/parkinsonism" element={<ParkinsonismPage />} />
              <Route path="/treatments/tonsillitis" element={<TonsillitisPage />} />
              <Route path="/treatments/ulcer" element={<UlcerPage />} />
              <Route path="/treatments/varicose-veins" element={<VaricoseVeinsPage />} />
              <Route path="/treatments/pcod" element={<PCODPage />} />
              <Route path="/treatments/acid-reflux" element={<AcidRefluxPage />} />
              <Route path="/treatments/gastritis" element={<GastritisPage />} />
              <Route path="/treatments/heart-burn" element={<HeartBurnPage />} />
              <Route path="/treatments/hormonal-imbalance" element={<HormonalImbalancePage />} />
              <Route path="/treatments/urticaria" element={<UrticariaPage />} />
              <Route path="/treatments/worm-infestation" element={<WormInfestationPage />} />
              <Route path="/treatments/cellulitis" element={<CellulitisPage />} />
              <Route path="/treatments/constipation" element={<ConstipationPage />} />
              <Route path="/treatments/fibroids" element={<FibroidsPage />} />
              <Route path="/treatments/white-discharge" element={<WhiteDischargePage />} />
              <Route path="/treatments/paralysis" element={<ParalysisPage />} />
              <Route path="/treatments/psoriasis" element={<PsoriasisPage />} />
              <Route path="/treatments/spondylosis" element={<SpondylosisPage />} />
              <Route path="/treatments/arthritis" element={<ArthritisPage />} />
              <Route path="/treatments/anal-fissure" element={<AnalFissurePage />} />
              <Route path="/treatments/calcaneal-spur" element={<CalcanealSpurPage />} />
              <Route path="/treatments/diabetic-wound" element={<DiabeticWoundPage />} />
              <Route path="/treatments/dandruff" element={<DandruffPage />} />
              <Route path="/treatments/digestive-disorders" element={<DigestiveDisordersPage />} />
              <Route path="/treatments/dyslipidemia" element={<DyslipidemiaPage />} />
              <Route path="/treatments/female-disorders" element={<FemaleDisordersPage />} />
              <Route path="/treatments/fungal-infection" element={<FungalInfectionPage />} />
              <Route path="/treatments/gall-bladder-stones" element={<GallBladderStonesPage />} />
              <Route path="/treatments/hair-fall" element={<HairFallPage />} />
              <Route path="/treatments/hypertension" element={<HypertensionPage />} />
              <Route path="/treatments/hydrocele" element={<HydrocelePage />} />
              <Route path="/treatments/infertility" element={<InfertilityPage />} />
              <Route path="/treatments/kidney-stones" element={<KidneyStonesPage />} />
              <Route path="/treatments/leucoderma" element={<LeucodermaPage />} />
              <Route path="/treatments/lipoma" element={<LipomaPage />} />
              <Route path="/treatments/piles" element={<PilesPage />} />
              <Route path="/treatments/sinusitis" element={<SinusitisPage />} />
              <Route path="/treatments/thyroid" element={<ThyroidPage />} />
              <Route path="/treatments/trigeminal-neuralgia" element={<TrigeminalNeuralgiaPage />} />
              <Route path="/treatments/varicose-ulcer" element={<VaricoseUlcerPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
